/**
 * one-stop/blocks-hero — the blocks that make up the hero band, plus the footer.
 *
 *   context-banner · search · identity-panel · help-contact
 *
 * Lifted from `Lakeview OneStop.dc.html`: the white `radius-md` search card with
 * its `shadow-lg`, the chip row, and the sign-in / welcome-back panel beside it
 * on a `minmax(0,1.35fr) minmax(0,1fr)` grid — stacking at 640 px exactly as the
 * prototype's 390 px layout does. `help-contact` is the prototype's four-column
 * footer band on the derived footer shade.
 */
import { useState } from "react";
import type { JSX } from "react";
import { Button, Chip, Icon, Input } from "@v0y4ge/jrni-ui";
import type { PortalIdentityFragment, PortalTicketState } from "@v0y4ge/api-contracts";
import {
  configBool,
  configChips,
  configInt,
  configLinkGroups,
  configString,
  configText,
  formatMoney,
  formatZonedTime,
  hrefOf,
  nestedConfigBool,
  nestedConfigText,
} from "./config";
import { CtaFeedback, fragmentOf } from "./primitives";
import { SignInDialog, useOneStopSession } from "./session";
import { uiStrings } from "./strings";
import type { StringKey } from "./strings";
import type { BlockRenderProps } from "./types";

/**
 * A control that is disabled because the flow does not exist yet has no call to
 * be in flight, so its feedback line is always the reason. Named rather than
 * inlined five times.
 */
const NOT_RUNNING = { status: "idle" } as const;

/* ── context-banner ──────────────────────────────────────────── */

/**
 * NOTE ON `activeFrom` / `activeTo` (both registry-REQUIRED on this type):
 * this renderer does NOT evaluate them. The document decides which blocks exist;
 * the renderer renders what it is given. A client clock is also the wrong
 * authority for a window authored in the tenant's zone. Raised with CC in the
 * milestone-2 reply — if the resolver is meant to drop an out-of-window banner,
 * that belongs server-side; if it is meant to be client-side, it has to be said,
 * so the two halves do not each assume the other did it.
 */
export function ContextBannerBlock({ block, ctx }: BlockRenderProps): JSX.Element | null {
  const text = configText(block.config, "text", ctx.locale);
  if (!text) return null;
  const tone = configString(block.config, "tone", "info");
  const link = block.config["link"];
  const href = hrefOf(link, ctx.locale);
  const label =
    link && typeof link === "object"
      ? configText(link as Record<string, unknown>, "label", ctx.locale)
      : null;
  return (
    <div className={tone === "highlight" ? "lv-banner lv-highlight" : "lv-banner"}>
      {text}
      {href && label ? (
        <>
          {" "}
          <a href={href}>{label}</a>
        </>
      ) : null}
    </div>
  );
}

/* ── search ─────────────────────────────────────────────────── */

/**
 * The search block owns the CARD; the host owns the STATE. `ctx.search` is the
 * controller the portal page hands in (value, submit, clear, status). The builder
 * host passes `null`: the same card renders, inert, so an admin sees the real
 * component rather than a placeholder — and cannot fire a live query from a
 * preview.
 */
export function SearchBlock({ block, ctx }: BlockRenderProps): JSX.Element {
  const t = uiStrings(ctx.locale);
  const search = ctx.search;
  const title = configText(block.config, "title", ctx.locale);
  const subtitle = configText(block.config, "subtitle", ctx.locale);
  const placeholder = configText(block.config, "placeholder", ctx.locale) ?? t("search.placeholder");
  const submitLabel = configText(block.config, "findHelpLabel", ctx.locale) ?? t("search.findHelp");
  const chipsOnly = configBool(block.config, "chipsOnly", false);
  const chips = configChips(block.config, ctx.locale, configInt(block.config, "maxChips", 6));
  const searching = search?.status === "searching";

  return (
    <div className="lv-searchcard">
      {title ? <span className="lv-panel-title">{title}</span> : null}
      {subtitle ? <span className="lv-soft">{subtitle}</span> : null}
      {chipsOnly ? null : (
        <form
          className="lv-searchrow"
          onSubmit={(event) => {
            event.preventDefault();
            if (search) search.onSubmit(search.value);
          }}
        >
          <div className="lv-searchfield">
            <Input
              type="search"
              size="lg"
              full
              /* The magnifier is what makes the field read as search before
                 anyone reads the placeholder — and it is the page's primary
                 affordance. Same component, same `size="lg"` box; the prototype
                 passes the same glyph. */
              iconLeft={Icon.search({ size: 16 })}
              value={search?.value ?? ""}
              placeholder={placeholder}
              aria-label={t("search.aria")}
              disabled={search === null}
              onChange={(event) => search?.onChange(event.target.value)}
            />
          </div>
          <span className="lv-act">
            {/* `lg`, like the Input it sits beside and the `Clear search` ghost
                that appears next to it once a search is open. The prototype's
                export carried the component default here and the parity note
                used to defend it, but that note predates the clear button: with
                three controls in the row, the page's primary affordance was the
                only short one. The Input is the element the eye measures the
                row against, so the row takes the Input's size. */}
            <Button hierarchy="primary" size="lg" type="submit" disabled={search === null || searching}>
              {searching ? t("cta.working") : submitLabel}
            </Button>
          </span>
          {search !== null && search.status !== "idle" ? (
            <span className="lv-act">
              <Button variant="ghost" size="lg" type="button" onClick={search.onClear}>
                {t("search.clear")}
              </Button>
            </span>
          ) : null}
        </form>
      )}
      {chips.length > 0 ? (
        <div className="lv-chips">
          {chips.map((chip) => (
            <span className="lv-chipwrap" key={`${block.id}-${chip.label}`}>
              <Chip
                disabled={search === null}
                onClick={() => {
                  if (!search) return;
                  search.onChange(chip.label);
                  search.onSubmit(chip.query);
                }}
              >
                {chip.label}
              </Chip>
            </span>
          ))}
        </div>
      ) : null}
      {search !== null && search.status === "error" ? (
        <p className="lv-cta-error" role="status">
          {t("search.error")} {search.errorMessage ?? ""}
        </p>
      ) : null}
    </div>
  );
}

/* ── identity-panel ──────────────────────────────────────────── */

/** ui-config — one sentence per live-ticket state. */
const TICKET_STATE_KEY: Record<PortalTicketState, StringKey> = {
  waiting: "identity.ticket.waiting",
  called: "identity.ticket.called",
  "being-served": "identity.ticket.being-served",
};

/**
 * Two variants of one block, chosen by the RESPONSE, never by a toggle: the
 * anonymous flavor carries no `data`, so the signed-out variant renders from
 * `config.signedOut`; the session flavor carries the `identity` fragment.
 *
 * A `null` in that fragment is authoritative. `resolvedIdentity` null ⇒ no EID is
 * rendered (the tenant has `showEid` off, or there was no clean match).
 * `balance` null ⇒ the hold renders WITHOUT an amount — not "$0", not a
 * placeholder. Nothing is re-derived and nothing is substituted.
 */
export function IdentityPanelBlock({ block, ctx }: BlockRenderProps): JSX.Element | null {
  const identity = fragmentOf(block, "identity");
  return identity ? (
    <SignedInPanel block={block} ctx={ctx} identity={identity} />
  ) : (
    <SignedOutPanel block={block} ctx={ctx} />
  );
}

function SignedOutPanel({ block, ctx }: BlockRenderProps): JSX.Element {
  const t = uiStrings(ctx.locale);
  const config = block.config;
  const session = useOneStopSession();
  const [signInOpen, setSignInOpen] = useState(false);
  const eyebrow = nestedConfigText(config, ["signedOut", "eyebrow"], ctx.locale);
  const title =
    nestedConfigText(config, ["signedOut", "title"], ctx.locale) ?? t("identity.signedOut.title");
  // `signedOut.body` is registry-REQUIRED and was authored, stored and never
  // rendered by milestone 1. Closed here.
  const body = nestedConfigText(config, ["signedOut", "body"], ctx.locale);
  const buttonLabel =
    nestedConfigText(config, ["signedOut", "buttonLabel"], ctx.locale) ??
    t("identity.signedOut.button");
  const showGuestHint = nestedConfigBool(config, ["signedOut", "showGuestHint"], true);
  const guestHint = nestedConfigText(config, ["signedOut", "guestHint"], ctx.locale);
  const findMyVisit =
    nestedConfigText(config, ["signedOut", "findMyVisitLabel"], ctx.locale) ?? t("identity.findVisit");

  return (
    <div className="lv-panel">
      <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
        {eyebrow ? <span className="lv-panel-eyebrow">{eyebrow}</span> : null}
        <span className="lv-panel-title">{title}</span>
        {/*
          `signedOut.body` — registry-REQUIRED, authored and stored since
          milestone 1, and until now never rendered anywhere. It sits directly
          under the title (operator ruling 2026-09-01) because it is the sentence
          that answers the title: the title says what this panel is, the body says
          what signing in gets you, and the button comes after both.

          `lv-soft` rather than a new class: it is this package's muted body text
          and it is already re-coloured for the tinted search card, so the panel
          reads correctly on both surfaces without a second declaration.

          Still conditional despite being required. Required is enforced at
          PUBLISH, so a draft in the builder preview legitimately has no body yet,
          and an empty <span> there would open a gap under every title.
        */}
        {body ? <span className="lv-soft">{body}</span> : null}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 9 }}>
        {/*
          Sign-in is the session flavor's ENTRY, and it is LIVE: pressing this
          opens the sheet that lists the tenant's demo personas, or asks for an
          email and a one-time code where there is no chooser. The host decides
          which — this button only opens it.

          It still renders disabled in the Journey Builder preview, where nothing
          may be called, and the reason is a `.lv-cta-reason` line rather than a
          `title`: a `title` is a hover tooltip and on a touch device it does not
          exist at all.
        */}
        <Button
          hierarchy="primary"
          full
          disabled={!session.canSignIn}
          onClick={() => setSignInOpen(true)}
        >
          {buttonLabel}
        </Button>
        <CtaFeedback
          t={t}
          disabledReason={session.canSignIn ? null : t("cta.preview")}
          state={NOT_RUNNING}
        />
        {/* A session that ended says so HERE, where the sign-in button is — the
            page changed under the viewer and that is worth one sentence. */}
        {session.notice === "sessionEnded" ? (
          <span className="lv-cta-error" style={{ display: "block" }} role="status">
            {t("identity.sessionEnded")}
          </span>
        ) : null}
        {body ? <span className="lv-soft">{body}</span> : null}
      </div>
      {showGuestHint && (guestHint || findMyVisit) ? (
        <div className="lv-panel-rule">
          {guestHint ? <span className="lv-soft">{guestHint}</span> : null}
          <button type="button" className="lv-linkbtn" disabled>
            {findMyVisit}
          </button>
          <CtaFeedback t={t} disabledReason={t("cta.pending.lookup")} state={NOT_RUNNING} />
        </div>
      ) : null}
      <SignInDialog
        open={signInOpen}
        locale={ctx.locale}
        onClose={() => setSignInOpen(false)}
        onSignedIn={(handle) => {
          setSignInOpen(false);
          session.onSignedIn(handle);
        }}
      />
    </div>
  );
}

function SignedInPanel(
  props: BlockRenderProps & { identity: PortalIdentityFragment },
): JSX.Element {
  const { block, ctx, identity } = props;
  const t = uiStrings(ctx.locale);
  const config = block.config;
  const session = useOneStopSession();
  const eyebrow =
    nestedConfigText(config, ["signedIn", "eyebrow"], ctx.locale) ?? t("identity.eyebrow");
  const template = nestedConfigText(config, ["signedIn", "greetingTemplate"], ctx.locale);
  const greeting = identity.greetingName
    ? template
      ? template.replace("{name}", identity.greetingName)
      : t("identity.greeting", { name: identity.greetingName })
    : t("identity.greetingGeneric");
  const ticketCta =
    nestedConfigText(config, ["signedIn", "ticketCtaLabel"], ctx.locale) ?? t("identity.ticketCta");
  const visitsCta =
    nestedConfigText(config, ["signedIn", "myVisitsCtaLabel"], ctx.locale) ?? t("identity.visitsCta");
  const showTicket = nestedConfigBool(config, ["signedIn", "showLiveTicket"], true);
  const showNext = nestedConfigBool(config, ["signedIn", "showNextAppointment"], true);
  const ticket = showTicket ? identity.ticket : null;

  return (
    <div className="lv-panel">
      <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
          <span className="lv-panel-eyebrow">{eyebrow}</span>
          <span className="lv-panel-title">{greeting}</span>
        </div>
        <div style={{ flex: "none", display: "flex", alignItems: "center", gap: 8 }}>
          {/* The demo badge comes off the establishing response's own `demo`
              flag, so it disappears with the tenant's demo window rather than
              with a client-side toggle. */}
          {session.session?.demo ? <span className="lv-note">{t("identity.demo")}</span> : null}
          {/* null ⇒ showEid is off, or no clean match. Render nothing at all. */}
          {identity.resolvedIdentity ? (
            <span className="lv-note">{identity.resolvedIdentity.eid}</span>
          ) : null}
        </div>
      </div>

      {ticket ? (
        <div
          style={{
            border: "1px solid var(--jrni-color-primary-2)",
            background: "var(--jrni-color-primary-4)",
            borderRadius: "var(--jrni-radius-default)",
            padding: 12,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 12,
          }}
        >
          <span style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
            <span
              style={{
                fontSize: "var(--jrni-font-size-P2)",
                fontWeight: "var(--jrni-font-weight-bold)",
                color: "var(--jrni-color-chrome-ink)",
              }}
            >
              {t(TICKET_STATE_KEY[ticket.state], { queue: ticket.queueName ?? "" })}
            </span>
            {ticket.estimatedWaitMinutes !== null ? (
              <span style={{ fontSize: "var(--jrni-font-size-L1)", color: "var(--lv-chrome-rule)" }}>
                {t("queue.joined.wait", { n: ticket.estimatedWaitMinutes })}
              </span>
            ) : null}
          </span>
          {ticket.peopleAhead !== null ? (
            <span style={{ flex: "none", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <span
                className="lv-bignum"
                style={{ fontSize: "var(--jrni-font-size-H2)", color: "var(--jrni-color-primary-text)" }}
              >
                {ticket.peopleAhead}
              </span>
              <span style={{ fontSize: "var(--jrni-font-size-L1)", color: "var(--lv-chrome-rule)" }}>
                {t("identity.ahead")}
              </span>
            </span>
          ) : null}
        </div>
      ) : null}

      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {showNext && identity.nextAppointment ? (
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
            <span style={{ fontSize: "var(--jrni-font-size-L1)", color: "var(--jrni-color-neutral-2)" }}>
              {t("identity.nextAppointment")}
            </span>
            <span
              style={{
                fontSize: "var(--jrni-font-size-L1)",
                fontWeight: "var(--jrni-font-weight-bold)",
                color: "var(--jrni-color-chrome-ink)",
              }}
            >
              {formatZonedTime(identity.nextAppointment, ctx.locale, {
                withDate: true,
                tenantTimeZone: ctx.timeZone,
              })}
            </span>
          </div>
        ) : null}
        {identity.holds?.holds.map((hold, i) => (
          <div
            key={`${block.id}-hold-${i}`}
            style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}
          >
            <span
              style={{
                fontSize: "var(--jrni-font-size-L1)",
                color: "var(--jrni-color-status-warning-text)",
                fontWeight: "var(--jrni-font-weight-semibold)",
              }}
            >
              {hold.label ?? hold.category}
            </span>
            {/* null balance ⇒ the hold renders without an amount, deliberately. */}
            {hold.balance ? (
              <span
                style={{
                  fontSize: "var(--jrni-font-size-L1)",
                  fontWeight: "var(--jrni-font-weight-bold)",
                  color: "var(--jrni-color-status-warning-text)",
                }}
              >
                {formatMoney(hold.balance, ctx.locale)}
              </span>
            ) : null}
          </div>
        ))}
      </div>

      {/* `degraded` is DATA, not an error: the SIS snapshot could not be read, and
          that is different from "no holds". */}
      {identity.degraded ? <span className="lv-soft">{t("identity.degraded")}</span> : null}

      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        <div className="lv-actions">
          <span className="lv-act" style={{ flex: "1 1 0" }}>
            <Button hierarchy="primary" full disabled>
              {ticketCta}
            </Button>
          </span>
          <span className="lv-act" style={{ flex: "1 1 0" }}>
            <Button hierarchy="secondary" full disabled>
              {visitsCta}
            </Button>
          </span>
        </div>
        {/* One reason line for the pair — both are the same visit lookup, and it
            is a visible line rather than a hover tooltip for the same reason as
            the signed-out panel above. */}
        <CtaFeedback t={t} disabledReason={t("cta.pending.lookup")} state={NOT_RUNNING} />
      </div>

      {/* Sign out is the session's EXIT and belongs beside the identity it ends.
          The host revokes the session and re-reads the public page; it clears its
          copy of the token either way, so a network failure cannot leave a
          student holding a live credential. */}
      <div className="lv-panel-rule">
        <button type="button" className="lv-linkbtn" onClick={session.onSignOut}>
          {t("identity.signOut")}
        </button>
      </div>
    </div>
  );
}

/* ── help-contact ─────────────────────────────────────────────── */

/**
 * The prototype's footer: four columns on the derived footer shade, collapsing to
 * one column on the chrome-ink band at 390 px. It is a BLOCK, not chrome — a page
 * whose document has no `help-contact` simply has no footer, which is what the
 * document said.
 */
export function HelpContactBlock({ block, ctx }: BlockRenderProps): JSX.Element | null {
  const groups = configLinkGroups(block.config, ctx.locale);
  const hours = configText(block.config, "hoursText", ctx.locale);
  const contact = block.config["contact"];
  const asRecord = contact && typeof contact === "object" ? (contact as Record<string, unknown>) : null;
  const address = asRecord ? configText(asRecord, "address", ctx.locale) : null;
  const room = asRecord ? configText(asRecord, "room", ctx.locale) : null;
  const phone = asRecord ? configText(asRecord, "phone", ctx.locale) : null;
  const title = configText(block.config, "title", ctx.locale);
  const hasContact = Boolean(address || room || phone || hours);
  if (groups.length === 0 && !hasContact) return null;

  return (
    <footer className="lv-footer">
      <div className="lv-footer-grid">
        {hasContact ? (
          <div className="lv-footer-col">
            {title ? <strong>{title}</strong> : null}
            {room ? <span>{room}</span> : null}
            {address ? <span>{address}</span> : null}
            {hours ? <span>{hours}</span> : null}
            {phone ? <span>{phone}</span> : null}
          </div>
        ) : null}
        {groups.map((group, i) => (
          <div className="lv-footer-col" key={`${block.id}-group-${i}`}>
            {group.title ? <strong>{group.title}</strong> : null}
            {group.links.map((link, j) =>
              link.href ? (
                <a key={`${block.id}-link-${i}-${j}`} href={link.href}>
                  {link.label}
                </a>
              ) : (
                <span key={`${block.id}-link-${i}-${j}`}>{link.label}</span>
              ),
            )}
          </div>
        ))}
      </div>
    </footer>
  );
}
